import React, { useState } from 'react';
import axios from 'axios';
import { Upload, LogOut, Check, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CreatorUpload() {
  const { user, logout } = useAuth();
  const [singleUpload, setSingleUpload] = useState({ content_url: '', affiliate_link: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleSingleUpload = async (e) => {
    e.preventDefault();
    
    if (!singleUpload.content_url) {
      toast.error('Please provide a content URL');
      return;
    }

    try {
      setUploading(true);
      await axios.post(`${API}/videos`, singleUpload, { withCredentials: true });
      toast.success('Video uploaded successfully! Our team will start tracking it.');
      setSingleUpload({ content_url: '', affiliate_link: '' });
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0e27] via-[#1a1f3a] to-[#0a0e27] p-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Creator Upload Portal</h1>
            <p className="text-gray-400 mt-1">Submit your video for tracking</p>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <User className="w-4 h-4" />
                <span>{user.name}</span>
              </div>
            )}
            <Button
              onClick={logout}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Form */}
      <div className="max-w-2xl mx-auto">
        <div className="glass-card rounded-xl p-8">
          <form onSubmit={handleSingleUpload} className="space-y-6">
            <div>
              <Label htmlFor="content_url" className="text-gray-300 text-lg">Video URL *</Label>
              <Input
                id="content_url"
                type="url"
                placeholder="https://www.instagram.com/reel/..."
                value={singleUpload.content_url}
                onChange={(e) => setSingleUpload({ ...singleUpload, content_url: e.target.value })}
                required
                className="mt-3 bg-white/5 border-white/10 text-white text-lg py-6"
              />
              <p className="text-xs text-gray-500 mt-2">Instagram, TikTok, or YouTube video URL</p>
            </div>

            <div>
              <Label htmlFor="affiliate_link" className="text-gray-300 text-lg">
                Affiliate Link
              </Label>
              <Input
                id="affiliate_link"
                type="url"
                placeholder="https://app.emergent.sh/?via=YOUR_NAME"
                value={singleUpload.affiliate_link}
                onChange={(e) => setSingleUpload({ ...singleUpload, affiliate_link: e.target.value })}
                className="mt-3 bg-white/5 border-white/10 text-white text-lg py-6"
              />
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-7"
            >
              {uploading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </span>
              ) : uploadSuccess ? (
                <span className="flex items-center justify-center space-x-2">
                  <Check className="w-5 h-5" />
                  <span>Uploaded Successfully!</span>
                </span>
              ) : (
                <span className="flex items-center justify-center space-x-2">
                  <Upload className="w-5 h-5" />
                  <span>Submit Video</span>
                </span>
              )}
            </Button>
          </form>

          {/* Instructions */}
          <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-300 mb-3">📋 How it works</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>1. Paste your video URL (Instagram, TikTok, or YouTube)</p>
              <p>2. Add your affiliate link</p>
              <p>3. Click Submit to upload your video</p>
            </div>
          </div>

          {uploadSuccess && (
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-300 text-center font-medium">
                ✅ Video submitted! You'll receive tracking updates from the Emergent team.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreatorUpload;
